import React, { useState } from 'react';
import { Box, Typography, IconButton, Paper } from '@mui/material';
import {
  InsertDriveFile as FileIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Done as DoneIcon,
  DoneAll as DoneAllIcon,
  Block as BlockIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { ChatMessage } from '../../types/chat';
import ChatAvatar from './ChatAvatar';
import ImagePreviewModal from './ImagePreviewModal';

// Get API base URL for file proxy (remove trailing slash if present)
const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000').replace(/\/$/, '');

// Convert relative API file URLs to full URLs
const getFullFileUrl = (fileUrl: string): string => {
  if (fileUrl.startsWith('/api/')) {
    return `${API_BASE_URL}${fileUrl}`;
  }
  return fileUrl;
};

interface MessageItemProps {
  message: ChatMessage;
  isOwnMessage: boolean;
  showAvatar?: boolean;
  onDelete?: (messageId: number) => void;
}

// Outlook blue theme
const OUTLOOK_BLUE = '#0078D4';

const MessageItem: React.FC<MessageItemProps> = ({
  message,
  isOwnMessage,
  showAvatar = true,
  onDelete,
}) => {
  const [previewImage, setPreviewImage] = useState<{ url: string; name: string } | null>(null);

  const formatTime = (dateString: string) => {
    try {
      return format(new Date(dateString), 'HH:mm', { locale: fr });
    } catch {
      return '';
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const isImage = (fileType: string) => fileType.startsWith('image/');

  const handleImageClick = (url: string, name: string) => {
    setPreviewImage({ url, name });
  };

  const renderAttachment = (attachment: ChatMessage['attachments'][0]) => {
    if (isImage(attachment.file_type)) {
      return (
        <Box
          key={attachment.id}
          sx={{
            mt: 1,
            maxWidth: 250,
            borderRadius: 1,
            overflow: 'hidden',
          }}
        >
          <img
            src={getFullFileUrl(attachment.file_url)}
            alt={attachment.file_name}
            style={{
              width: '100%',
              maxHeight: 200,
              objectFit: 'cover',
              cursor: 'pointer',
            }}
            onClick={() => handleImageClick(getFullFileUrl(attachment.file_url), attachment.file_name)}
          />
        </Box>
      );
    }

    return (
      <Paper
        key={attachment.id}
        elevation={0}
        sx={{
          mt: 1,
          p: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          backgroundColor: isOwnMessage ? 'rgba(255,255,255,0.1)' : 'action.hover',
          borderRadius: 1,
          maxWidth: 250,
        }}
      >
        <FileIcon sx={{ color: isOwnMessage ? 'white' : 'text.secondary' }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: isOwnMessage ? 'white' : 'inherit',
            }}
          >
            {attachment.file_name}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: isOwnMessage ? 'rgba(255,255,255,0.7)' : 'text.secondary' }}
          >
            {formatFileSize(attachment.file_size)}
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={() => window.open(getFullFileUrl(attachment.file_url), '_blank')}
          sx={{ color: isOwnMessage ? 'white' : 'text.secondary' }}
        >
          <DownloadIcon fontSize="small" />
        </IconButton>
      </Paper>
    );
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: isOwnMessage ? 'row-reverse' : 'row',
          alignItems: 'flex-start',
          gap: 1,
          mb: 1.5,
          px: 1,
        }}
      >
        {/* Avatar or spacer for alignment */}
        {!isOwnMessage && (
          showAvatar && message.sender ? (
            <ChatAvatar
              name={message.sender.full_name}
              isOnline={message.sender.is_online}
              lastSeenAt={message.sender.last_seen_at}
              size={32}
              showStatus={false}
            />
          ) : (
            <Box sx={{ width: 32, minWidth: 32 }} /> // Spacer to maintain alignment
          )
        )}

        {/* Message content */}
        <Box
          sx={{
            maxWidth: '70%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: isOwnMessage ? 'flex-end' : 'flex-start',
          }}
        >
          {/* Sender name (for received messages) */}
          {!isOwnMessage && message.sender && showAvatar && (
            <Typography
              variant="caption"
              sx={{ mb: 0.5, color: 'text.secondary', fontWeight: 500 }}
            >
              {message.sender.full_name}
            </Typography>
          )}

          {/* Message bubble */}
          {message.is_deleted ? (
            // Deleted message placeholder
            <Box
              sx={{
                backgroundColor: 'background.default',
                borderRadius: 2,
                borderTopRightRadius: isOwnMessage ? 4 : 16,
                borderTopLeftRadius: isOwnMessage ? 16 : 4,
                px: 1.5,
                py: 1,
                border: 1,
                borderColor: 'divider',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <BlockIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography
                  variant="body2"
                  sx={{
                    fontStyle: 'italic',
                    color: 'text.secondary',
                  }}
                >
                  {isOwnMessage ? 'Vous avez supprimé ce message' : 'Ce message a été supprimé'}
                </Typography>
              </Box>
            </Box>
          ) : (
            // Normal message
            <Box
              sx={{
                backgroundColor: isOwnMessage ? OUTLOOK_BLUE : 'background.default',
                color: isOwnMessage ? 'white' : 'inherit',
                borderRadius: 2,
                borderTopRightRadius: isOwnMessage ? 4 : 16,
                borderTopLeftRadius: isOwnMessage ? 16 : 4,
                px: 1.5,
                py: 1,
                position: 'relative',
                '&:hover .delete-btn': {
                  opacity: 1,
                },
              }}
            >
              {/* Text content */}
              {message.content && (
                <Typography
                  variant="body2"
                  sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                >
                  {message.content}
                </Typography>
              )}

              {/* Attachments */}
              {message.attachments.map(renderAttachment)}

              {/* Delete button (for own messages) */}
              {isOwnMessage && onDelete && (
                <IconButton
                  className="delete-btn"
                  size="small"
                  onClick={() => onDelete(message.id)}
                  sx={{
                    position: 'absolute',
                    top: -8,
                    left: -8,
                    opacity: 0,
                    transition: 'opacity 0.2s',
                    backgroundColor: 'background.paper',
                    boxShadow: 1,
                    '&:hover': { backgroundColor: 'error.light' },
                  }}
                >
                  <DeleteIcon fontSize="small" color="error" />
                </IconButton>
              )}
            </Box>
          )}

          {/* Time and read status */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              mt: 0.5,
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', fontSize: '0.7rem' }}
            >
              {formatTime(message.created_at)}
            </Typography>
            {/* Message status checkmarks - only for own messages */}
            {/* ✓ = sent, ✓✓ gray = delivered, ✓✓ blue = read */}
            {isOwnMessage && (
              message.is_read ? (
                <DoneAllIcon
                  sx={{
                    fontSize: 14,
                    color: '#34B7F1', // Blue for read
                  }}
                />
              ) : (
                <DoneIcon
                  sx={{
                    fontSize: 14,
                    color: '#999', // Gray for sent
                  }}
                />
              )
            )}
          </Box>
        </Box>
      </Box>

      {/* Image Preview Modal */}
      {previewImage && (
        <ImagePreviewModal
          open={!!previewImage}
          imageUrl={previewImage.url}
          imageName={previewImage.name}
          onClose={() => setPreviewImage(null)}
        />
      )}
    </>
  );
};

export default MessageItem;
